import { Headphones, Volume2, VolumeX, Mic, Play } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ModelPicker } from "@/components/common/ModelPicker";
import { useLiveCoach, LEVELS, CAT_COLOR } from "@/context/LiveCoachContext";
import { cn } from "@/lib/utils";

const CATEGORIES = ["Positioning", "Awareness", "Gameplay Habits", "Match Coaching", "Post-Fight"];

export function LiveCoachPanel({ games = [] }) {
    const { enabled, setEnabled, level, setLevel, voice, setVoice, voiceUri, setVoiceUri, volume, setVolume, voices, testVoice, model, setModel, game, setGame, history, monitoring, active } = useLiveCoach();

    return (
        <div className="bg-surface border border-border rounded-sm overflow-hidden" data-testid="live-coach-panel">
            <div className={cn("flex items-center justify-between px-5 py-4 border-b border-border transition-colors", enabled && "bg-volt/[0.06]")}>
                <div className="flex items-center gap-3">
                    <span className={cn("grid place-items-center w-9 h-9 rounded-sm", enabled ? "bg-volt/15" : "bg-obsidian")}>
                        <Headphones className={cn("w-5 h-5", enabled ? "text-volt" : "text-muted-foreground")} />
                    </span>
                    <div>
                        <div className="font-heading font-bold text-white flex items-center gap-2">
                            Live AI Coach
                            {enabled && (active
                                ? <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-success"><span className="w-1.5 h-1.5 rounded-full bg-success ww-live-dot" /> Live</span>
                                : <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-warn"><span className="w-1.5 h-1.5 rounded-full bg-warn" /> Armed</span>)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {enabled && !monitoring ? "Starts automatically when monitoring goes live" : "Real-time verbal callouts while you play"}
                        </div>
                    </div>
                </div>
                <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="live-coach-toggle" className="data-[state=checked]:bg-volt" />
            </div>

            <div className="p-5 space-y-5">
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Coaching Level</label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                        {Object.entries(LEVELS).map(([key, cfg]) => (
                            <button key={key} onClick={() => setLevel(key)} data-testid={`live-level-${key}`}
                                className={cn("rounded-sm border px-2 py-2.5 text-center transition-colors",
                                    level === key ? "border-volt bg-volt/10" : "border-border hover:border-white/25")}>
                                <div className={cn("text-sm font-semibold", level === key ? "text-volt" : "text-white")}>{cfg.label}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">{cfg.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Game Context</label>
                        <Select value={game} onValueChange={setGame}>
                            <SelectTrigger className="bg-obsidian border-border rounded-sm h-10 mt-1.5" data-testid="live-coach-game"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-surface2 border-border">
                                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex-1">
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">AI Model</label>
                        <div className="mt-1.5"><ModelPicker value={model} onChange={setModel} /></div>
                    </div>
                </div>

                <button onClick={() => setVoice(!voice)} data-testid="live-coach-voice"
                    className="w-full flex items-center justify-between px-4 py-3 rounded-sm border border-border bg-obsidian hover:border-white/25 transition-colors">
                    <span className="flex items-center gap-2 text-sm text-white">
                        {voice ? <Volume2 className="w-4 h-4 text-volt" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
                        Voice Callouts {voice ? "On" : "Off"}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">{voice ? "Spoken aloud" : "Beep + visual"}</span>
                </button>

                {voice && (
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Coach Voice</label>
                        <div className="flex gap-2 mt-1.5">
                            <Select value={voiceUri || "__default__"} onValueChange={(v) => setVoiceUri(v === "__default__" ? "" : v)}>
                                <SelectTrigger className="flex-1 bg-obsidian border-border rounded-sm h-10" data-testid="live-coach-voice-select"><SelectValue placeholder="System default" /></SelectTrigger>
                                <SelectContent className="bg-surface2 border-border max-h-60">
                                    <SelectItem value="__default__">System Default</SelectItem>
                                    {voices.map((v) => <SelectItem key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <button type="button" onClick={testVoice} data-testid="live-coach-test-voice" title="Test voice"
                                className="h-10 px-3 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 flex items-center gap-1.5 text-sm font-semibold shrink-0">
                                <Play className="w-3.5 h-3.5" /> Test
                            </button>
                        </div>
                    </div>
                )}

                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                        <span>Coach Volume</span>
                        <span className="font-mono text-volt">{Math.round(volume * 100)}%</span>
                    </label>
                    <Slider value={[volume]} onValueChange={([v]) => setVolume(v)} min={0} max={1} step={0.05}
                        className="mt-3 [&_[role=slider]]:border-volt/60 [&>span>span]:bg-volt" data-testid="live-coach-volume" />
                </div>

                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Coaches On</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {CATEGORIES.map((c) => (
                            <span key={c} className="text-[11px] font-mono px-2 py-1 rounded-sm border" style={{ color: CAT_COLOR[c], borderColor: `${CAT_COLOR[c]}44`, background: `${CAT_COLOR[c]}10` }}>{c}</span>
                        ))}
                    </div>
                </div>

                {enabled && history.length > 0 && (
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Live Feed</label>
                        <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto" data-testid="live-coach-feed">
                            {history.map((c) => (
                                <div key={c.key} className="flex items-start gap-2 text-sm">
                                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: CAT_COLOR[c.category] || "#00C2FF" }} />
                                    <span className="text-foreground/85">{c.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
